import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface ReportStatusBadgeProps {
  status: 'draft' | 'review' | 'final' | null;
  size?: 'small' | 'medium';
  showIcon?: boolean;
  hardLockEnabled?: boolean;
}

const STATUS_CONFIG = {
  draft: {
    label: 'Generated',
    color: 'info' as const,
    icon: EditIcon,
    tooltip: 'Report has been generated and can be edited',
  },
  review: {
    label: 'Under Review',
    color: 'info' as const,
    icon: RateReviewIcon,
    tooltip: 'Report is under review and can still be edited',
  },
  final: {
    label: 'Finalized',
    labelEditable: 'Generated',
    color: 'success' as const,
    icon: LockIcon,
    iconEditable: CheckCircleIcon,
    tooltip: 'Report is finalized and locked - cannot be edited',
    tooltipEditable: 'Report has been generated - editing allowed',
  },
};

const ReportStatusBadge: React.FC<ReportStatusBadgeProps> = ({
  status,
  size = 'small',
  showIcon = false,
  hardLockEnabled = false,
}) => {
  if (!status) {
    return null;
  }

  const config = STATUS_CONFIG[status];
  // For finalized status, use different icon/label/tooltip based on hard lock setting
  const isFinal = status === 'final';
  const useGeneratedLabel = isFinal && !hardLockEnabled;
  const useEditable = isFinal && !hardLockEnabled;
  const Icon = useEditable && 'iconEditable' in config
    ? (config as typeof config & { iconEditable: typeof LockIcon }).iconEditable
    : config.icon;
  const tooltip = useEditable && 'tooltipEditable' in config
    ? (config as typeof config & { tooltipEditable: string }).tooltipEditable
    : config.tooltip;
  const label = useGeneratedLabel && 'labelEditable' in config
    ? (config as typeof config & { labelEditable: string }).labelEditable
    : config.label;

  return (
    <Tooltip title={tooltip} arrow>
      <Chip
        label={label}
        color={config.color}
        size={size}
        icon={showIcon ? <Icon sx={{ fontSize: 16 }} aria-hidden="true" /> : undefined}
        sx={{
          fontWeight: status === 'final' ? 600 : 500,
          cursor: 'help',
        }}
      />
    </Tooltip>
  );
};

export default ReportStatusBadge;
